/**
 * Tipos API del frontend-superadmin.
 *
 * Sprint 11 v2 S11-V2-11. Subset de los tipos del portal — solo lo
 * que las páginas super_admin necesitan. Mantener sincronizado con
 * `apps/backend/app/schemas/`.
 */

export interface PersonaPerfil {
  id: string;
  nombre_completo: string;
  email: string | null;
  rut: string;
}

export interface UsuarioPerfil {
  id: string;
  tipo_cuenta: 'operativa' | 'super_admin' | 'sistema';
  email_login: string;
  activo: boolean;
}

export interface AuthMeResponse {
  persona: PersonaPerfil;
  usuario: UsuarioPerfil;
  roles_vigentes: string[];
  tenencias_activas: unknown[];
}

/** Nivel de morosidad desde el cual se avisa al vecino (convención #20). */
export type NivelMinimoAviso = 'deudor' | 'moroso' | 'suspendible';

/** Envío del recordatorio semestral (convención #19: cerrado por defecto). */
export type ModoRecordatorio = 'cerrado' | 'manual' | 'automatico';

export interface ConfiguracionNotificaciones {
  id: string;
  dias_throttle_mismo_nivel: number;
  nivel_minimo_aviso: NivelMinimoAviso;
  aviso_al_suspender: boolean;
  recordatorio_semestral_envio: ModoRecordatorio;
  recordatorio_semestral_dia: number;
  vigente_desde: string;
  aprobado_acta_url: string | null;
  creado_at: string;
  actualizado_at: string;
}

export interface ConfiguracionNotificacionesCreate {
  dias_throttle_mismo_nivel: number;
  nivel_minimo_aviso: NivelMinimoAviso;
  aviso_al_suspender: boolean;
  recordatorio_semestral_envio: ModoRecordatorio;
  recordatorio_semestral_dia: number;
  vigente_desde: string;
  aprobado_acta_url?: string | null;
}

// Pagos — Sprint 12 S12-03. Subset alineado a `app/schemas/pago.py::PagoRead`.

export type EstadoPago =
  | 'por_distribuir'
  | 'distribuido'
  | 'aprobado'
  | 'rechazado';

// Codigos validos del filtro `banco` en GET /pagos/?banco=. Backend
// los mapea a TODOS los TEMPLATE_ID de ese banco via BANCOS_CONOCIDOS
// (Sprint 12 S12-02; multi-template desde 2026-08). Mantener sincronizado
// con `BANCOS_CONOCIDOS` de `apps/backend/app/api/pagos.py`.
export const BANCOS_CODIGOS = [
  'banco_chile',
  'banco_consorcio',
  'banco_estado',
  'banco_falabella',
  'bci',
  'itau',
  'mach',
  'santander',
  'scotiabank',
] as const;
export type BancoCodigo = (typeof BANCOS_CODIGOS)[number];

export interface PagoRead {
  id: string;
  lote_id: string | null;
  persona_id: string | null;
  monto_total: number;
  fecha_transferencia: string;
  num_operacion: string;
  remitente_nombre: string;
  remitente_email: string | null;
  mensaje_email: string;
  email_template_detectado: string;
  comprobante_url: string | null;
  estado: EstadoPago;
  compensacion_id: string | null;
  aprobado_por_usuario_id: string | null;
  aprobado_at: string | null;
  creado_at: string;
  actualizado_at: string;
}

export interface ListPagosFiltros {
  estado?: EstadoPago;
  lote_id?: string;
  banco?: BancoCodigo;
  search?: string;
}

// Lotes — Sprint 12 S12-04. `LoteResumen` del backend.

export interface LoteResumen {
  id: string;
  identificador: string;
  direccion: string;
  sector: string;
  activo: boolean;
  notas: string | null;
}

// Sprint 13 S13-10: sectores fijados para Calbucura.
export type Sector = 'Oriente' | 'Poniente';

// Sprint 13 S13-11: mantenedor de lotes (CRUD del super_admin).
export interface LoteCreate {
  identificador: string;
  direccion: string;
  sector: Sector;
  notas?: string | null;
}

export interface LoteUpdate {
  direccion?: string;
  sector?: Sector;
  notas?: string | null;
}

// Cuotas — Sprint 12 S12-04. Subset de `CuotaRead` del backend.

export type EstadoCuota =
  | 'pendiente'
  | 'en_abono'
  | 'pagada'
  | 'vencida'
  | 'condonada';

export interface CuotaRead {
  id: string;
  lote_id: string;
  tenencia_responsable_id: string;
  periodo: string;
  monto_total: number;
  vence_el: string;
  estado: EstadoCuota;
  es_arrastre: boolean;
  motivo_condonacion: string | null;
  creado_at: string;
  actualizado_at: string;
}

// Categorias — Sprint 12 S12-04. Subset de `CategoriaRead`.

export type TipoCategoria = 'ingreso' | 'gasto';

export interface CategoriaRead {
  id: string;
  nombre: string;
  tipo: TipoCategoria;
  color: string;
  activa: boolean;
  es_fondo_terceros?: boolean;
  categoria_padre_id: string | null;
}

// Clasificar pago multi-concepto — Sprint 11 v2 S11-V2-10, consumido
// por la UI tesorero del Sprint 12 S12-04.

export type TipoDistribucion =
  | 'cuota'
  | 'multa'
  | 'otro_ingreso'
  | 'venta_recurso'
  | 'aporte_proyecto'
  | 'saldo_a_favor';

export interface LineaDistribucionInput {
  tipo: TipoDistribucion;
  descripcion: string;
  monto_aplicado: number;
  cuota_id?: string | null;
  recurso_id?: string | null;
  proyecto_id?: string | null;
  categoria_id?: string | null;
}

export interface ClasificarPagoInput {
  lote_id: string;
  lineas: LineaDistribucionInput[];
  aprender_alias?: boolean;
}

export interface ClasificarPagoResponse {
  pago: PagoRead;
  ingresos_varios_creados: number;
  alias_aprendido: boolean;
  email_enviado: boolean;
}

export interface SugerenciaDistribucionPropuesta {
  lote_id: string;
  persona_id: string;
  cuota_id: string;
  monto: number;
  ruta: string;
}

export interface SugerenciaDistribucionRead {
  sugerencia: SugerenciaDistribucionPropuesta | null;
  motivo: string | null;
}

// Personas + roles — Sprint 12 S12-05.

export interface PersonaConRoles {
  id: string;
  nombre_completo: string;
  rut: string | null;
  email: string | null;
  celular: string | null;
  activo: boolean;
  usuario_id: string | null;
  tipo_cuenta: 'operativa' | 'super_admin' | 'sistema' | null;
  roles_vigentes: string[];
  lote_identificador: string | null;
  lote_sector: string | null;
}

export interface ListPersonasFiltros {
  search?: string;
  con_rol?: string;
  activo?: boolean;
}

// Sprint 13 S13-12 + S13-17: mantenedor de personas (CRUD).
// RUT opcional al crear, editable por admin via PATCH.
export interface PersonaCreate {
  nombre_completo: string;
  rut?: string | null;
  email?: string | null;
  celular?: string | null;
  fecha_nacimiento?: string | null;
  es_test?: boolean;
}

export interface PersonaUpdate {
  nombre_completo?: string;
  rut?: string | null;
  email?: string | null;
  celular?: string | null;
  fecha_nacimiento?: string | null;
}

export interface PersonaRead {
  id: string;
  nombre_completo: string;
  rut: string | null;
  email: string | null;
  celular: string | null;
  fecha_nacimiento: string | null;
  activo: boolean;
  es_test: boolean;
}

// Roles asignables por API (Sprint 12 S12-17: simplificacion de 6 a
// 4 roles del sistema). super_admin excluido a proposito (convencion
// #1: solo administracion@calbucura.cl, no se asigna por API).
export const ROLES_ASIGNABLES = [
  'vecino',
  'comision',
  'tesorero',
] as const;
export type RolAsignable = (typeof ROLES_ASIGNABLES)[number];

// Etiqueta legible (lenguaje usuario-facing, convencion #13).
// `super_admin` se incluye solo para renderizar la pill en personas
// que ya lo tienen; no es opcion del selector de asignar.
export const ETIQUETA_ROL: Record<string, string> = {
  vecino: 'Vecino',
  comision: 'Comisión',
  tesorero: 'Tesorero',
  super_admin: 'Administrador',
};

export interface PersonaRolRead {
  id: string;
  rol: string;
  desde_fecha: string;
  hasta_fecha: string | null;
  otorgado_por_usuario: string;
  otorgado_at: string;
  revocado_por_usuario: string | null;
  revocado_at: string | null;
  motivo_revocacion: string | null;
  vigente: boolean;
}

export interface AsignarRolInput {
  rol: RolAsignable;
  desde_fecha?: string | null;
  // Email para provisionar el login si la persona aún no tiene usuario
  // operativo (crea cuenta Auth + Usuario). Se ignora si ya tiene login.
  email?: string | null;
}

export interface RevocarRolInput {
  motivo_revocacion?: string | null;
}

// Tenencias — Sprint 12 S12-06. Para la UI super_admin de cerrar
// tenencia (flujo ex-vecino).

export const TIPOS_TENENCIA = [
  'propietario_residente',
  'propietario_no_residente',
  'arrendatario',
  'comodatario',
  'otro',
] as const;
export type TipoTenencia = (typeof TIPOS_TENENCIA)[number];

// Etiqueta usuario-facing por tipo (decisiones cerradas en S12-17).
export const ETIQUETA_TIPO_TENENCIA: Record<string, string> = {
  propietario_residente: 'Propietario residente',
  propietario_no_residente: 'Propietario no residente',
  arrendatario: 'Arrendatario',
  comodatario: 'Comodato',
  otro: 'Otro vínculo',
};

export interface TenenciaConPersonaLote {
  id: string;
  lote_id: string;
  lote_identificador: string;
  lote_sector: string;
  persona_id: string;
  persona_nombre_completo: string;
  persona_rut: string;
  persona_activa: boolean;
  tipo: string;
  desde: string;
  hasta: string | null;
  es_actual: boolean;
  responsable_pago_cuotas: boolean;
  notificar_morosidad: boolean;
  motivo_cierre: string | null;
  creado_at: string;
  actualizado_at: string;
}

export interface ListTenenciasFiltros {
  lote_id?: string;
  persona_id?: string;
  activas?: boolean;
  sector?: string;
}

// Sprint 13 S13-09 + S13-13: crear tenencia (mantenedor sa).
export type TipoTenenciaLiteral =
  | 'propietario_residente'
  | 'propietario_no_residente'
  | 'arrendatario'
  | 'comodatario'
  | 'otro';

export const ETIQUETA_TIPO_TENENCIA_SA: Record<TipoTenenciaLiteral, string> = {
  propietario_residente: 'Propietario residente',
  propietario_no_residente: 'Propietario no residente',
  arrendatario: 'Arrendatario',
  comodatario: 'Comodato',
  otro: 'Otro vínculo',
};

export interface TenenciaCreate {
  lote_id: string;
  persona_id: string;
  tipo: TipoTenenciaLiteral;
  desde: string;
  responsable_pago_cuotas?: boolean;
  responsable_desde_periodo?: string | null;
  acceso_portal_habilitado?: boolean;
  doc_respaldo_url?: string | null;
  notas?: string | null;
}

export interface TenenciaReadSa {
  id: string;
  lote_id: string;
  persona_id: string;
  tipo: string;
  desde: string;
  hasta: string | null;
  es_actual: boolean;
  responsable_pago_cuotas: boolean;
  motivo_cierre: string | null;
  creado_at: string;
  actualizado_at: string;
}

export interface TenenciaCreadaResponse {
  tenencia: TenenciaReadSa;
  rol_vecino_otorgado: boolean;
  motivo_no_otorgado: string | null;
}

export interface CerrarTenenciaInput {
  fecha_cierre: string;
  motivo_cierre?: string | null;
}

export interface CerrarTenenciaResponse {
  tenencia: TenenciaConPersonaLote;
  persona_desactivada: boolean;
}

// Documentos del barrio — Sprint 12 S12-07. La UI super_admin de
// publicar consume estos tipos (Sprint 12 S12-10).

export type TipoDocumento = 'reglamento' | 'acta' | 'informe' | 'otro';

export const TIPOS_DOCUMENTO = ['reglamento', 'acta', 'informe', 'otro'] as const;

export const ETIQUETA_TIPO_DOCUMENTO: Record<TipoDocumento, string> = {
  reglamento: 'Reglamento',
  acta: 'Acta',
  informe: 'Informe',
  otro: 'Otro',
};

export interface DocumentoRead {
  id: string;
  titulo: string;
  descripcion: string | null;
  url_externa: string;
  tipo: TipoDocumento;
  publicado_at: string;
  vigente_desde: string;
  vigente_hasta: string | null;
  creado_por_usuario_id: string;
  creado_at: string;
  actualizado_at: string;
}

export interface DocumentoCreate {
  titulo: string;
  descripcion?: string | null;
  url_externa: string;
  tipo: TipoDocumento;
  vigente_desde: string;
  vigente_hasta?: string | null;
}

// Avisos del barrio — Sprint 12 S12-08.

export type TipoAviso = 'informativo' | 'urgente' | 'evento';

export const TIPOS_AVISO = ['informativo', 'urgente', 'evento'] as const;

export const ETIQUETA_TIPO_AVISO: Record<TipoAviso, string> = {
  informativo: 'Informativo',
  urgente: 'Urgente',
  evento: 'Evento',
};

export interface AvisoRead {
  id: string;
  titulo: string;
  cuerpo: string;
  tipo: TipoAviso;
  publicado_at: string;
  vigente_hasta: string | null;
  creado_por_usuario_id: string;
  creado_at: string;
  actualizado_at: string;
}

export interface AvisoCreate {
  titulo: string;
  cuerpo: string;
  tipo: TipoAviso;
  vigente_hasta?: string | null;
}

export interface AvisoCreadoResponse {
  aviso: AvisoRead;
  fan_out_emails_enviados: number;
  fan_out_emails_fallidos: number;
  fan_out_notificaciones_creadas: number;
}

// Compensaciones (pago en especie) — Sprint 12 S12-14 + S12-15.

export interface CompensacionRead {
  id: string;
  lote_id: string;
  tenencia_id: string;
  descripcion: string;
  categoria_id: string;
  monto: number;
  periodicidad: 'mensual' | 'bimestral' | 'semestral' | 'anual';
  proyecto_id: string | null;
  vigente_desde: string;
  vigente_hasta: string | null;
  aprobado_acta_url: string | null;
  notas: string | null;
  activa: boolean;
  creado_at: string;
  actualizado_at: string;
}

export interface CompensacionCreate {
  lote_id: string;
  tenencia_id: string;
  descripcion: string;
  categoria_id: string;
  monto: number;
  periodicidad: 'mensual' | 'bimestral' | 'semestral' | 'anual';
  proyecto_id?: string | null;
  vigente_desde: string;
  vigente_hasta?: string | null;
  aprobado_acta_url?: string | null;
  notas?: string | null;
  activa?: boolean;
}

export interface LineaCompensacionInput {
  cuota_id: string;
  monto: number;
}

export interface AplicarCompensacionInput {
  periodo: string;
  fecha_aplicacion: string;
  descripcion_gasto: string;
  proveedor: string;
  lineas: LineaCompensacionInput[];
}

export interface AplicarCompensacionResponse {
  compensacion: CompensacionRead;
  gasto: {
    id: string;
    monto: number;
    fecha: string;
    proveedor: string;
    descripcion: string;
  };
  pago_virtual: {
    id: string;
    monto_total: number;
    num_operacion: string;
    estado: string;
    compensacion_id: string;
  };
  distribuciones_creadas: number;
  cuotas_actualizadas: string[];
}
